import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { Container, Card, Alert } from 'react-bootstrap';
import { SettingsPage } from '../components/settings/settings-page';
import { UserProfileUIStore } from '../stores/user-profile-ui-store';
import { UserStore } from '../stores/user-store';
import { LoadingStore } from '../stores/loading-store';

interface SettingsContainerProps {
  userProfileUIStore?: UserProfileUIStore;
  userStore?: UserStore;
  loadingStore?: LoadingStore;
}

interface SettingsContainerState {
  successMessage: string | null;
  errorMessage: string | null;
}

@inject('userProfileUIStore', 'userStore', 'loadingStore')
@observer
export class SettingsContainer extends React.Component<
  SettingsContainerProps,
  SettingsContainerState
> {
  state: SettingsContainerState = {
    successMessage: null,
    errorMessage: null,
  };

  componentDidMount() {
    const { userProfileUIStore, userStore } = this.props;

    // Load current user and user profile if not already loaded
    if (!userStore?.currentUser?.subject) {
      userStore?.loadCurrentUser();
    }

    if (!userProfileUIStore?.currentUserProfile) {
      userProfileUIStore?.loadCurrentUserProfile();
    }
  }

  handleLogoUploaded = async (logoUrl: string) => {
    const { userProfileUIStore } = this.props;

    if (!userProfileUIStore?.currentUserProfile) {
      this.setState({
        errorMessage: 'Error: user profile not loaded',
      });
      return;
    }

    try {
      userProfileUIStore.updateLogo(logoUrl);
      await userProfileUIStore.saveCurrentUserProfile();

      this.setState({
        successMessage: 'Logo uploaded and saved successfully!',
        errorMessage: null,
      });
    } catch (error) {
      console.error('Error saving logo URL:', error);
      this.setState({
        errorMessage: 'Error saving logo. Please try again.',
        successMessage: null,
      });
    }
  };

  handleLogoRemoved = async () => {
    const { userProfileUIStore } = this.props;

    if (!userProfileUIStore?.currentUserProfile) {
      this.setState({
        errorMessage: 'Error: user profile not loaded',
      });
      return;
    }

    try {
      userProfileUIStore.removeLogo();
      await userProfileUIStore.saveCurrentUserProfile();

      this.setState({
        successMessage: 'Logo removed successfully!',
        errorMessage: null,
      });
    } catch (error) {
      console.error('Error removing logo:', error);
      this.setState({
        errorMessage: 'Error removing logo. Please try again.',
        successMessage: null,
      });
    }
  };

  handlePreparedByChange = async (value: string) => {
    const { userProfileUIStore } = this.props;
    if (userProfileUIStore) {
      userProfileUIStore.updatePreparedBy(value);
      // We don't save immediately on every keystroke, the user will click "Save" or we can auto-save on blur?
      // For now let's implement a save button/action in the page component,
      // OR we can just have a simple "Save Settings" button.
      // Given the existing logo upload was immediate, but this is a text field.
      // Let's assume we want a save action for this field.
      // Actually, let's keep it simple: The SettingsPage will have a "Save" button for the text fields.
    }
  };

  handleSaveSettings = async () => {
    const { userProfileUIStore } = this.props;
    if (!userProfileUIStore?.currentUserProfile) return;

    try {
      await userProfileUIStore.saveCurrentUserProfile();
      this.setState({
        successMessage: 'Settings saved successfully!',
        errorMessage: null,
      });
    } catch (error) {
      console.error('Error saving settings:', error);
      this.setState({
        errorMessage: 'Error saving settings.',
        successMessage: null,
      });
    }
  };

  handleError = (error: string) => {
    this.setState({
      errorMessage: error,
      successMessage: null,
    });
  };

  dismissAlert = (type: 'success' | 'error') => {
    if (type === 'success') {
      this.setState({ successMessage: null });
    } else {
      this.setState({ errorMessage: null });
    }
  };

  render() {
    const { userProfileUIStore } = this.props;
    const { successMessage, errorMessage } = this.state;

    if (!userProfileUIStore?.currentUserProfile) {
      return (
        <Container className="mt-4">
          <Card>
            <Card.Body>
              <div className="text-center">
                <p>Loading user profile...</p>
              </div>
            </Card.Body>
          </Card>
        </Container>
      );
    }

    return (
      <Container fluid className="mt-4">
        <Card>
          <Card.Header>Settings</Card.Header>
          <Card.Body>
            {successMessage && (
              <Alert
                variant="success"
                dismissible
                onClose={() => this.dismissAlert('success')}
              >
                <div className="text-center m-2">{successMessage}</div>
              </Alert>
            )}

            {errorMessage && (
              <Alert
                variant="danger"
                dismissible
                onClose={() => this.dismissAlert('error')}
              >
                <div className="text-center m-2">{errorMessage}</div>
              </Alert>
            )}

            <SettingsPage
              userProfile={userProfileUIStore.currentUserProfile}
              userProfileUIStore={userProfileUIStore}
              onLogoUploaded={this.handleLogoUploaded}
              onLogoRemoved={this.handleLogoRemoved}
              onPreparedByChange={this.handlePreparedByChange}
              onSaveSettings={this.handleSaveSettings}
              onError={this.handleError}
            />
          </Card.Body>
        </Card>
      </Container>
    );
  }
}
